import TurndownService from "turndown";

// ── Format detection ──

// Old TipTap messages always wrap content in paired HTML tags (e.g. <p>...</p>).
// We require a matched opening+closing pair to avoid false positives when users
// type literal angle brackets like "<p>" in a markdown message.
const HTML_PAIRED_TAG_REGEX =
  /<(p|strong|em|b|i|s|u|a|code|pre|blockquote|ul|ol|li|spoiler|h[1-6]|del)\b[^>]*>[\s\S]*?<\/\1>/i;
// Self-closing / void tags that TipTap also produces
const HTML_VOID_TAG_REGEX = /<(?:br|hr)\s*\/?>/i;

/**
 * Detect whether content is HTML (old TipTap messages) or markdown (new messages).
 */
export function isHtmlContent(content: string): boolean {
  // Strip <details> blocks before checking — they legitimately contain HTML tags
  // (e.g. <strong> in <summary>) in markdown messages, which would false-positive.
  const withoutDetails = content.replace(/<details[\s\S]*?<\/details>/gi, "");
  return (
    HTML_PAIRED_TAG_REGEX.test(withoutDetails) ||
    HTML_VOID_TAG_REGEX.test(withoutDetails)
  );
}

// ── Turndown (HTML → Markdown) for legacy messages ──

const turndownService = new TurndownService({
  headingStyle: "atx",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
  strongDelimiter: "**",
});

// Custom rule for <spoiler> → ||text||
turndownService.addRule("spoiler", {
  filter: (node: HTMLElement) => node.nodeName.toLowerCase() === "spoiler",
  replacement(content: string) {
    return `||${content}||`;
  },
});

// Preserve <details> blocks as raw HTML so rehype-raw can render them.
// Without this, Turndown strips the tags and flattens the content.
turndownService.addRule("details", {
  filter: ["details"],
  replacement(_content, node) {
    return "\n\n" + node.outerHTML + "\n\n";
  },
});

// Custom rule for <s> and <del> → ~~text~~
turndownService.addRule("strikethrough", {
  filter: (node: HTMLElement) => {
    const tag = node.nodeName.toLowerCase();
    return tag === "s" || tag === "del";
  },
  replacement(content: string) {
    return `~~${content}~~`;
  },
});

/**
 * Convert HTML to markdown.
 * Used for:
 * - Rendering old HTML messages (convert before passing to react-markdown)
 * - Editing old messages (populate textarea with markdown)
 */
export function htmlToMarkdown(html: string): string {
  if (!html.trim()) {
    return "";
  }
  return turndownService.turndown(html);
}

/**
 * Normalize content to markdown.
 * If content is HTML (old messages), converts to markdown.
 * If content is already markdown (new messages), returns as-is.
 */
export function toMarkdown(content: string): string {
  if (!content.trim()) {
    return "";
  }
  return isHtmlContent(content) ? htmlToMarkdown(content) : content;
}

/**
 * Convert content (HTML or markdown) to plain text for previews.
 * Strips all formatting: HTML tags, markdown syntax, and replaces spoilers with a masked string.
 */
// Spoiler mask uses middle-dot characters to avoid being consumed by
// the bold / italic markdown-stripping regexes that look for asterisks.
const SPOILER_MASK = "······";

export function toPlainText(content: string): string {
  if (!content.trim()) {
    return "";
  }

  let text = content;

  if (isHtmlContent(text)) {
    // Replace HTML spoiler tags with masked text
    text = text.replace(/<spoiler\b[^>]*>.*?<\/spoiler>/gi, SPOILER_MASK);
    // Strip remaining HTML tags
    text = text.replace(/<[^>]*>?/gm, "");
  }

  // Mask raw HTML spoiler spans before stripping tags, so previews show "······"
  // instead of leaking the hidden text. Matches class="spoiler" or data-spoiler="true".
  text = text.replace(
    /<span\b[^>]*(?:class=["'][^"']*spoiler[^"']*["']|data-spoiler=["']true["'])[^>]*>[\s\S]*?<\/span>/gi,
    SPOILER_MASK,
  );

  // Strip raw HTML tags that may appear in markdown (e.g. <details>, <summary>).
  // Negative lookahead after tag name excludes autolinks like <https://…> and <user@…>.
  text = text.replace(/<\/?[a-z][a-z0-9]*\b(?![:@])[^>]*>/gi, "");

  // Strip block-level markdown (headings, horizontal rules, blockquotes)
  text = text
    .replace(/^#{1,6}(?!#)\s*/gm, "")
    .replace(/^[-*_]{3,}\s*$/gm, "")
    .replace(/^>\s*/gm, "");

  // Strip markdown syntax (bold, italic, strikethrough, inline code, links)
  text = text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");

  // Replace markdown spoiler syntax ||text|| with masked text.
  // Done after stripping other markdown so the mask stays intact.
  text = text.replace(/\|\|(.+?)\|\|/g, SPOILER_MASK).trim();

  return text;
}

export type MarkdownFormat =
  | "heading"
  | "bold"
  | "italic"
  | "strikethrough"
  | "code"
  | "codeBlock"
  | "link"
  | "bulletList"
  | "numberedList"
  | "blockquote"
  | "horizontalRule"
  | "spoiler";

/**
 * Apply markdown formatting around the selected text in a textarea.
 * Returns the new text and cursor positions.
 */
export function applyMarkdownFormat(
  text: string,
  selectionStart: number,
  selectionEnd: number,
  format: MarkdownFormat,
): { newText: string; newSelectionStart: number; newSelectionEnd: number } {
  const before = text.slice(0, selectionStart);
  const selected = text.slice(selectionStart, selectionEnd);
  const after = text.slice(selectionEnd);

  let newText: string;
  let newSelectionStart: number;
  let newSelectionEnd: number;

  switch (format) {
    case "heading": {
      const lineStart = text.lastIndexOf("\n", selectionStart - 1) + 1;
      const lineEnd = text.indexOf("\n", selectionEnd);
      const actualLineEnd = lineEnd === -1 ? text.length : lineEnd;
      const currentLine = text.slice(lineStart, actualLineEnd);

      const headingMatch = currentLine.match(/^(#{1,6})\s/);
      let newLine: string;

      if (headingMatch) {
        const currentLevel = headingMatch[1].length;
        if (currentLevel < 6) {
          newLine = currentLine.replace(
            /^#{1,6}\s/,
            "#".repeat(currentLevel + 1) + " ",
          );
        } else {
          newLine = currentLine.replace(/^#{1,6}\s/, "");
        }
      } else {
        newLine = "# " + currentLine;
      }

      newText = text.slice(0, lineStart) + newLine + text.slice(actualLineEnd);
      newSelectionStart =
        selectionStart + (newLine.length - currentLine.length);
      newSelectionEnd = selectionEnd + (newLine.length - currentLine.length);
      break;
    }

    case "bold": {
      const prefix = "**";
      const suffix = "**";
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    case "italic": {
      const prefix = "*";
      const suffix = "*";
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    case "strikethrough": {
      const prefix = "~~";
      const suffix = "~~";
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    case "code": {
      if (selected.includes("\n")) {
        const prefix = "```\n";
        const suffix = "\n```";
        newText = before + prefix + selected + suffix + after;
        newSelectionStart = selectionStart + prefix.length;
        newSelectionEnd = selectionEnd + prefix.length;
      } else {
        const prefix = "`";
        const suffix = "`";
        newText = before + prefix + selected + suffix + after;
        newSelectionStart = selectionStart + prefix.length;
        newSelectionEnd = selectionEnd + prefix.length;
      }
      break;
    }

    case "link": {
      let prefix: string;
      let suffix: string;
      if (selected) {
        prefix = "[";
        suffix = "](url)";
      } else {
        prefix = "[link text](";
        suffix = ")";
      }
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    case "bulletList": {
      const lines = selected ? selected.split("\n") : [""];
      const formattedLines = lines.map((line) => {
        if (line.trim().startsWith("- ")) {
          return line.replace(/^(\s*)- /, "$1");
        }
        return line ? `- ${line}` : "-";
      });
      const formatted = formattedLines.join("\n");
      newText = before + formatted + after;
      newSelectionStart = selectionStart;
      newSelectionEnd = selectionStart + formatted.length;
      break;
    }

    case "numberedList": {
      const lines = selected ? selected.split("\n") : [""];
      const formattedLines = lines.map((line, index) => {
        if (line.trim().match(/^\d+\.\s/)) {
          return line.replace(/^(\s*)\d+\.\s/, "$1");
        }
        return line ? `${index + 1}. ${line}` : "1.";
      });
      const formatted = formattedLines.join("\n");
      newText = before + formatted + after;
      newSelectionStart = selectionStart;
      newSelectionEnd = selectionStart + formatted.length;
      break;
    }

    case "codeBlock": {
      const prefix = "```\n";
      const suffix = "\n```";
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    case "blockquote": {
      const lines = selected ? selected.split("\n") : [""];
      const formattedLines = lines.map((line) => {
        if (line.startsWith("> ")) {
          return line.slice(2);
        }
        return `> ${line}`;
      });
      const formatted = formattedLines.join("\n");
      newText = before + formatted + after;
      newSelectionStart = selectionStart;
      newSelectionEnd = selectionStart + formatted.length;
      break;
    }

    case "horizontalRule": {
      const needsBlankBefore = before.length > 0 && !before.endsWith("\n\n");
      const needsNewlineBefore =
        !needsBlankBefore && before.length > 0 && !before.endsWith("\n");
      const prefix =
        (needsBlankBefore ? "\n\n" : needsNewlineBefore ? "\n" : "") +
        "---\n\n";
      newText = before + prefix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionStart + prefix.length;
      break;
    }

    case "spoiler": {
      const prefix = "||";
      const suffix = "||";
      newText = before + prefix + selected + suffix + after;
      newSelectionStart = selectionStart + prefix.length;
      newSelectionEnd = selectionEnd + prefix.length;
      break;
    }

    default:
      newText = text;
      newSelectionStart = selectionStart;
      newSelectionEnd = selectionEnd;
  }

  return { newText, newSelectionStart, newSelectionEnd };
}
